import { BirdIcon, BracesIcon, FileTypeIcon, TextAlignStartIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JsonViewer } from '~/modules/common/json-viewer';
import { ToggleGroup, ToggleGroupItem } from '~/modules/ui/toggle-group';
import { lazyNamed } from '~/utils/lazy-named';
import type { GenRequest, GenSchema, GenSchemaProperty } from './types';

// Lazy: shiki and its grammars load when the code view opens, not with the docs route.
const CodeViewer = lazyNamed(() => import('./code-viewer'), 'CodeViewer');

type SchemaViewMode = 'format' | 'zod' | 'type' | 'example';

interface ViewerGroupProps {
  schema: GenSchema | GenSchemaProperty | GenRequest;
  /** Code to display in zod mode */
  zodCode?: string;
  /** Code to display in type mode */
  typeCode?: string;
  /** Example JSON to display in example mode */
  example?: unknown;
  defaultInspectDepth?: number;
  defaultViewMode?: SchemaViewMode;
}

export function ViewerGroup({
  schema,
  zodCode,
  typeCode,
  example,
  defaultInspectDepth = 5,
  defaultViewMode = 'format',
}: ViewerGroupProps) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<SchemaViewMode>(defaultViewMode);

  const toggleItems: {
    value: SchemaViewMode;
    icon: typeof TextAlignStartIcon;
    label: string;
    ariaLabel: string;
    show: boolean;
  }[] = [
    {
      value: 'format',
      icon: TextAlignStartIcon,
      label: t('c:docs.format'),
      ariaLabel: t('c:docs.view_format'),
      show: true,
    },
    {
      value: 'example',
      icon: BirdIcon,
      label: t('c:example'),
      ariaLabel: t('c:docs.view_example'),
      show: example !== undefined,
    },
    {
      value: 'zod',
      icon: BracesIcon,
      label: t('c:docs.zod'),
      ariaLabel: t('c:docs.view_zod'),
      show: !!zodCode,
    },
    {
      value: 'type',
      icon: FileTypeIcon,
      label: t('c:type'),
      ariaLabel: t('c:docs.view_type'),
      show: !!typeCode,
    },
  ];

  return (
    <div className="relative">
      <ToggleGroup
        type="single"
        value={viewMode}
        onValueChange={(value: string | string[]) => value && setViewMode(value as SchemaViewMode)}
        size="xs"
        variant="outline"
        className="absolute top-2 right-2 z-10 bg-muted/50 max-md:hidden"
      >
        {toggleItems
          .filter((item) => item.show)
          .map(({ value, icon: Icon, label, ariaLabel }) => (
            <ToggleGroupItem
              key={value}
              value={value}
              aria-label={ariaLabel}
              className="opacity-50 hover:opacity-70 data-pressed:opacity-100"
            >
              <Icon className="mr-1.5 h-4 w-4" />
              <span className="text-xs lowercase">{label}</span>
            </ToggleGroupItem>
          ))}
      </ToggleGroup>
      <div className="overflow-x-auto rounded-md bg-muted/50">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={viewMode}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="p-3 md:py-6"
          >
            {viewMode === 'format' && (
              <JsonViewer
                value={schema}
                showKeyQuotes={false}
                openapiMode="schema"
                rootName={false}
                defaultInspectDepth={defaultInspectDepth}
                indentWidth={3}
              />
            )}
            <Suspense fallback={null}>
              {viewMode === 'zod' && zodCode && <CodeViewer code={zodCode} language="zod" />}
              {viewMode === 'type' && typeCode && <CodeViewer code={typeCode} language="typescript" />}
            </Suspense>
            {viewMode === 'example' && example !== undefined && (
              <JsonViewer value={example} rootName={false} defaultInspectDepth={defaultInspectDepth} indentWidth={3} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
